import { useEffect, useRef, type RefObject } from 'react';

/**
 * The keyboard and focus behaviour every modal in this app shares.
 *
 * Extracted from `ConfirmDialog` when a second modal appeared, because this is
 * the part that is easy to get wrong and expensive to get wrong twice: two
 * copies drift, and the copy somebody forgot to fix is the one a keyboard user
 * gets stuck in.
 *
 * What it guarantees, and why each one is not optional:
 *   - Escape closes. Otherwise a dialog opened by accident has to be dismissed
 *     with the mouse, which is the one thing a keyboard user cannot do.
 *   - Tab cycles WITHIN the panel. Without the trap, Tab walks into the page
 *     behind — still there, still scrollable, and now unreachable by mouse.
 *   - Focus returns to whatever opened it. Without this, acting on row 40 of a
 *     500-row table sends you back to the top of the page.
 *   - The page behind does not scroll under the panel.
 */
export function useModalBehaviour<T extends HTMLElement>({
  open,
  panel,
  onClose,
}: {
  open: boolean;
  panel: RefObject<T | null>;
  onClose: () => void;
}): void {
  const openedBy = useRef<HTMLElement | null>(null);

  /*
   * The handler is held in a ref so the effect below depends on `open` ALONE.
   * Callers pass an inline arrow, which is a new function on every render, and
   * a re-render of the screen behind (a poll finishing, a keystroke in the
   * search box) would otherwise tear the effect down and set it up again —
   * pulling focus back to the first control while somebody was tabbing.
   */
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;

    // Remember who opened it, so focus can go back there on close.
    openedBy.current = document.activeElement as HTMLElement | null;

    const focusables = () =>
      Array.from(
        panel.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );

    // THE FIRST CONTROL, which every modal here orders so that it is a safe
    // one: a stray Enter on a dialog that has only just appeared must never be
    // the thing that unlinks a pod or leaves for a provider.
    focusables()[0]?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !panel.current?.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      openedBy.current?.focus?.();
    };
    // `open` ALONE, deliberately: `panel` is a ref object and stable for the
    // life of the component, and `close` is a ref for the reason given above.
  }, [open]);
}
